










import { defineComponent, ref, toRefs, onMounted, onUnmounted, getCurrentInstance } from '@vue/composition-api';
import _ from 'lodash';
import fs from 'fs';
import i18n, { i18n_t } from '@/locales/i18n';
import uploadHandler from '@/renderer/components/SpacePage/compositions/uploadHandler/index';
import { IS_BROWSER } from '@/common/constant';
import tips from '@/renderer/scripts/utils/tips';

interface DropFile extends File {
  fullPath: string;
}

export default defineComponent({
  props: {
    spaceId: {
      type: String,
      default: '',
    },
    quotaLimited: {
      type: Boolean,
      default: false,
    },
    path: {
      type: String,
      default: '',
    },
    isSync: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const vm = getCurrentInstance()?.proxy || context.root;
    const show = ref(false); // 是否展示拖拽区域
    const inDropArea = ref(false); // 鼠标是否正在拖拽在droparea区域内
    let dropArea: null | HTMLElement = null;

    // 缓存拖拽上传时当前空间的信息
    let spaceInfoCache = {
      spaceId: '',
      path: '',
    };

    const { methods: uploadMethods } = uploadHandler();

    const methods = {
      documentDragenter(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        show.value = true;
      },
      documentDragover(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        show.value = inDropArea.value;
      },
      documentDragleave(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (e.clientX === 0 && e.clientY === 0) {
          show.value = false;
        }
      },
      documentDrop(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      },
      dropAreaDragenter(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        inDropArea.value = true;
      },
      dropAreaDragleave(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        inDropArea.value = false;
        show.value = false;
      },
      async dropAreaDrop(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (!e.dataTransfer || e.dataTransfer.files.length === 0) {
          inDropArea.value = false;
          show.value = false;
          return;
        }
        const { path } = e.dataTransfer.files[0];
        // 如果是同步盘的话就不要上传文件，只用返回文件的路径
        if (props.isSync) {
          fs.stat(path, (err, state) => {
            if (state.isDirectory()) {
              context.emit('filePath', path);
              return;
            }
            tips.error(i18n_t('当前不支持将文件和快捷方式作为路径，请选择文件夹'));
          });
        } else {
          spaceInfoCache = {
            spaceId: props.spaceId,
            path: props.path,
          };
          // 处理上传
          methods.uploadFile(e.dataTransfer);
        }
        inDropArea.value = false;
        show.value = false;
      },
      async uploadFile(dataTransfer: DataTransfer) {
        if (IS_BROWSER) {
          let browserSupport = true;
          const fileItems = [...dataTransfer.items];
          const hasFolder = fileItems.some(item => {
            if (!item.webkitGetAsEntry) {
              browserSupport = false;
            }
            const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
            const isFolder = entry?.isDirectory;
            return isFolder;
          });
          if (hasFolder) {
            if (!browserSupport) {
              tips.error(i18n_t('当前浏览器不支持拖拽文件夹上传'));
              return;
            }
            const fileItemCopy = _.cloneDeep(fileItems);
            methods.uploadFolder(fileItemCopy);
          } else {
            const fileItem = [...dataTransfer.files];
            uploadMethods.dropFile(spaceInfoCache.path, fileItem, spaceInfoCache.spaceId);
          }
        } else {
          uploadMethods.dropFile(spaceInfoCache.path, [...dataTransfer.files], spaceInfoCache.spaceId);
        }
      },
      async uploadFolder(dataTransferItems: DataTransferItem[]) {
        const fileList: DropFile[] = [];
        const dirList: string[] = [];
        // 注意不能在for循环里取dataTransferItems[i].webkitGetAsEntry();
        const entryList = dataTransferItems.map(item => item.webkitGetAsEntry());
        for (let i = 0; i < dataTransferItems.length; i++) {
          // DOC: https://developer.mozilla.org/zh-CN/docs/Web/API/DataTransferItem/webkitGetAsEntry
          const entry = entryList[i];
          if (entry) {
            if (entry.isDirectory) {
              dirList.push(entry.name);
            }
            const { fileList: entryFileList } = await methods.getFiles(entry, '');
            fileList.push(...entryFileList);
          }
        }
        if (fileList.length === 0) {
          // 拖拽上传空文件夹直接提示
          tips.error(i18n_t('没有要上传的文件'));
          return;
        }
        const folderName =
          dirList.length > 1
            ? i18n_t('"{slot1}"等文件夹', {
                slot1: dirList[0],
              })
            : `"${dirList[0]}"`;
        const tip = `${i18n_t('将{slot1}个文件上传到此网站?', {
          slot1: fileList.length,
        })}\n${i18n_t('此操作会上传{folderName}下的所有文件。请仅在您信任该网站的情况下执行此操作。', {
          folderName,
        })}`;
        // eslint-disable-next-line no-alert
        if (window.confirm && window.confirm(tip)) {
          uploadMethods.dropFile(spaceInfoCache.path, fileList, spaceInfoCache.spaceId);
        }
      },
      async readDir(entry: any): Promise<any[]> {
        const dirReader = entry.createReader();
        const allFileEntries: any[] = [];
        // DOC: https://developer.mozilla.org/zh-CN/docs/Web/API/DataTransferItem/webkitGetAsEntry#javascript
        // 根据 MDN 上所说，在 Chrome 77 及以上版本的浏览器上
        // readEntries 方法一次最多读取 100 个 FileSystemEntry
        // 因此需要重复调用，直至读取完所有的数据
        while (true) {
          const entries = await new Promise<any[]>((resolve, reject) => {
            dirReader.readEntries(
              (entries: any[]) => resolve(entries),
              (e: any) => reject(e),
            );
          });
          if (entries.length === 0) {
            break;
          }
          allFileEntries.push(...entries);
        }
        return allFileEntries;
      },
      readFile(entry: any) {
        return new Promise((resolve, reject) => {
          entry.file(
            (file: File) => resolve(file),
            (e: any) => reject(e),
          );
        });
      },
      // 获取文件夹下的文件 (由于运行时无法识别 FileSystemEntry 类型，因此此处使用any)
      async getFiles(entry: any, path: string) {
        const fileList: DropFile[] = [];
        const queue: Array<{ path: string; entry: any }> = [{ path, entry }];
        while (queue.length > 0) {
          const item = queue.shift();
          if (item) {
            const { path, entry } = item;
            if (entry.isFile) {
              const file: File = (await methods.readFile(entry)) as File;
              const newFile = new File([file], file.name, {
                type: file.type,
              });
              fileList.push(Object.assign(newFile, { fullPath: path + file.name }));
            } else if (entry.isDirectory) {
              const entries = await methods.readDir(entry);
              for (let i = 0; i < entries.length; i++) {
                queue.push({ path: `${path + entry.name}/`, entry: entries[i] });
              }
            }
          }
        }
        return { fileList };
      },
    };

    onMounted(() => {
      dropArea = document.querySelector('.drop-area');
      document.addEventListener('dragenter', methods.documentDragenter, false);
      document.addEventListener('dragover', methods.documentDragover, false);
      document.addEventListener('dragleave', methods.documentDragleave, false);
      document.addEventListener('drop', methods.documentDrop, false);
      dropArea?.addEventListener('dragenter', methods.dropAreaDragenter, false);
      dropArea?.addEventListener('dragleave', methods.dropAreaDragleave, false);
      dropArea?.addEventListener('drop', methods.dropAreaDrop, false);
    });
    onUnmounted(() => {
      document.removeEventListener('dragenter', methods.documentDragenter, false);
      document.removeEventListener('dragover', methods.documentDragover, false);
      document.removeEventListener('dragleave', methods.documentDragleave, false);
      document.removeEventListener('drop', methods.documentDrop, false);
      dropArea?.removeEventListener('dragenter', methods.dropAreaDragenter, false);
      dropArea?.removeEventListener('dragleave', methods.dropAreaDragleave, false);
      dropArea?.removeEventListener('drop', methods.dropAreaDrop, false);
    });
    return {
      show,
    };
  },
});
